import { Head, Link } from "@inertiajs/react";
import {
  Box,
  Button,
  Callout,
  Card,
  Flex,
  Heading,
  Spinner,
  Switch,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useEffect, useMemo, useState } from "react";

import { AppLayout } from "@/components/app-layout";
import {
  disconnectIntegrationOauth,
  fetchIntegrationSettings,
  refreshIntegrationOauth,
  startIntegrationOauth,
  testIntegrationConnection,
  type IntegrationProviderSetting,
  updateIntegrationSetting,
} from "@/lib/api";
import type { SharedProps } from "@/lib/types";

type IntegrationsIndexProps = SharedProps;

export default function IntegrationsIndex({ auth }: IntegrationsIndexProps) {
  const [providers, setProviders] = useState<IntegrationProviderSetting[]>([]);
  const [enabledByProvider, setEnabledByProvider] = useState<Record<string, boolean>>({});
  const [draftByProvider, setDraftByProvider] = useState<
    Record<string, Record<string, string | boolean>>
  >({});
  const [savingByProvider, setSavingByProvider] = useState<Record<string, boolean>>({});
  const [testingByProvider, setTestingByProvider] = useState<Record<string, boolean>>({});
  const [errorByProvider, setErrorByProvider] = useState<Record<string, string>>({});
  const [testMessageByProvider, setTestMessageByProvider] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadSettings() {
      try {
        const data = await fetchIntegrationSettings();
        setProviders(data);
        setEnabledByProvider(
          data.reduce<Record<string, boolean>>((acc, provider) => {
            acc[provider.provider] = provider.enabled;
            return acc;
          }, {}),
        );
      } catch (error) {
        setErrorByProvider({
          page: error instanceof Error ? error.message : "Failed to load settings",
        });
      } finally {
        setLoading(false);
      }
    }

    loadSettings();
  }, []);

  const sortedProviders = useMemo(
    () => [...providers].sort((a, b) => a.label.localeCompare(b.label)),
    [providers],
  );

  function updateDraft(provider: string, key: string, value: string | boolean) {
    setDraftByProvider((previous) => ({
      ...previous,
      [provider]: {
        ...(previous[provider] || {}),
        [key]: value,
      },
    }));
  }

  async function connectProvider(provider: IntegrationProviderSetting) {
    setErrorByProvider((previous) => ({ ...previous, [provider.provider]: "" }));
    try {
      const authorizationUrl = await startIntegrationOauth(provider.provider);
      window.location.assign(authorizationUrl);
    } catch (error) {
      setErrorByProvider((previous) => ({
        ...previous,
        [provider.provider]: error instanceof Error ? error.message : "Failed to start OAuth flow",
      }));
    }
  }

  async function disconnectProvider(provider: IntegrationProviderSetting) {
    setErrorByProvider((previous) => ({ ...previous, [provider.provider]: "" }));
    try {
      await disconnectIntegrationOauth(provider.provider);
      const data = await fetchIntegrationSettings();
      setProviders(data);
    } catch (error) {
      setErrorByProvider((previous) => ({
        ...previous,
        [provider.provider]:
          error instanceof Error ? error.message : "Failed to disconnect provider",
      }));
    }
  }

  async function refreshProvider(provider: IntegrationProviderSetting) {
    setErrorByProvider((previous) => ({ ...previous, [provider.provider]: "" }));
    try {
      await refreshIntegrationOauth(provider.provider);
      const data = await fetchIntegrationSettings();
      setProviders(data);
    } catch (error) {
      setErrorByProvider((previous) => ({
        ...previous,
        [provider.provider]:
          error instanceof Error ? error.message : "Failed to refresh OAuth token",
      }));
    }
  }

  async function saveProvider(provider: IntegrationProviderSetting) {
    setSavingByProvider((previous) => ({ ...previous, [provider.provider]: true }));
    setErrorByProvider((previous) => ({ ...previous, [provider.provider]: "" }));

    try {
      const draft = draftByProvider[provider.provider] || {};
      const credentials = Object.entries(draft).reduce<Record<string, string | boolean>>(
        (acc, [key, value]) => {
          if (typeof value === "boolean") {
            acc[key] = value;
            return acc;
          }

          const trimmed = value.trim();
          if (trimmed.length > 0) acc[key] = trimmed;
          return acc;
        },
        {},
      );

      const updated = await updateIntegrationSetting(provider.provider, {
        enabled: enabledByProvider[provider.provider] ?? provider.enabled,
        config_method: provider.required_config_method,
        credentials,
      });

      setProviders((previous) =>
        previous.map((item) => (item.provider === updated.provider ? updated : item)),
      );
      setDraftByProvider((previous) => ({ ...previous, [provider.provider]: {} }));
    } catch (error) {
      setErrorByProvider((previous) => ({
        ...previous,
        [provider.provider]: error instanceof Error ? error.message : "Failed to save settings",
      }));
    } finally {
      setSavingByProvider((previous) => ({ ...previous, [provider.provider]: false }));
    }
  }

  async function testProvider(provider: IntegrationProviderSetting) {
    setTestingByProvider((previous) => ({ ...previous, [provider.provider]: true }));
    setErrorByProvider((previous) => ({ ...previous, [provider.provider]: "" }));
    setTestMessageByProvider((previous) => ({ ...previous, [provider.provider]: "" }));

    try {
      const result = await testIntegrationConnection(provider.provider);
      setTestMessageByProvider((previous) => ({
        ...previous,
        [provider.provider]: result.message,
      }));
    } catch (error) {
      setErrorByProvider((previous) => ({
        ...previous,
        [provider.provider]: error instanceof Error ? error.message : "Failed to test integration",
      }));
    } finally {
      setTestingByProvider((previous) => ({ ...previous, [provider.provider]: false }));
    }
  }

  return (
    <>
      <Head title="Integrations" />
      <AppLayout
        user={auth.user}
        activeModule="weekly_planner"
        onModuleChange={() => {}}
        pageTitle="Integrations"
        pageActions={
          <Link href="/">
            <Button variant="outline">Back to Dashboard</Button>
          </Link>
        }
      >
        <div className="space-y-4">
          {errorByProvider.page && (
            <Callout.Root color="red" variant="soft">
              <Callout.Text>{errorByProvider.page}</Callout.Text>
            </Callout.Root>
          )}

          {loading ? (
            <Flex
              align="center"
              justify="center"
              gap="2"
              className="rounded-lg border bg-background p-6"
            >
              <Spinner size="2" />
              <Text size="2" color="gray">
                Loading integration settings...
              </Text>
            </Flex>
          ) : (
            <div className="space-y-4">
              {sortedProviders.map((provider) => {
                const providerDraft = draftByProvider[provider.provider] || {};
                const isSaving = savingByProvider[provider.provider] || false;
                const isTesting = testingByProvider[provider.provider] || false;
                const isEnabled = enabledByProvider[provider.provider] ?? provider.enabled;

                return (
                  <Card key={provider.provider}>
                    <Box className="space-y-4 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <Heading size="4">{provider.label}</Heading>
                          <Text size="1" color="gray">
                            Required method:{" "}
                            <Text as="span" weight="medium">
                              {provider.required_config_method}
                            </Text>
                          </Text>
                          {provider.provider === "quickbooks" && (
                            <Text size="1" color="gray">
                              OAuth status:{" "}
                              <Text as="span" weight="medium">
                                {provider.oauth_connected ? "Connected" : "Not connected"}
                              </Text>
                            </Text>
                          )}
                        </div>
                        <Flex align="center" gap="2">
                          <Text size="2" weight="medium">
                            Enabled
                          </Text>
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={(checked) =>
                              setEnabledByProvider((previous) => ({
                                ...previous,
                                [provider.provider]: Boolean(checked),
                              }))
                            }
                          />
                        </Flex>
                      </div>

                      <div className="grid gap-3 md:grid-cols-2">
                        {provider.credential_fields.map((field) => (
                          <div key={field.key} className="space-y-1">
                            {field.input_type === "boolean" ? (
                              <>
                                <Flex align="center" gap="2">
                                  <Switch
                                    checked={Boolean(
                                      providerDraft[field.key] ?? provider.credentials[field.key],
                                    )}
                                    onCheckedChange={(checked) =>
                                      updateDraft(provider.provider, field.key, Boolean(checked))
                                    }
                                  />
                                  <Text size="2" weight="medium">
                                    {field.label}
                                  </Text>
                                </Flex>
                                <Text size="1" color="gray">
                                  Use sandbox for QuickBooks test company workflows; turn off for
                                  production.
                                </Text>
                              </>
                            ) : (
                              <>
                                <Text as="label" size="2" weight="medium">
                                  {field.label}
                                  {field.required ? " *" : ""}
                                </Text>
                                <TextField.Root
                                  type={field.secret ? "password" : "text"}
                                  value={String(providerDraft[field.key] ?? "")}
                                  placeholder={
                                    typeof provider.credentials[field.key] === "string"
                                      ? (provider.credentials[field.key] as string)
                                      : undefined
                                  }
                                  onChange={(event) =>
                                    updateDraft(provider.provider, field.key, event.target.value)
                                  }
                                />
                                <Text size="1" color="gray">
                                  {field.secret
                                    ? "Stored securely and masked after save."
                                    : "Non-secret field."}
                                </Text>
                              </>
                            )}
                          </div>
                        ))}
                      </div>

                      {provider.provider === "quickbooks" && (
                        <div className="rounded-md border p-3">
                          <Text size="2" weight="medium">
                            QuickBooks OAuth
                          </Text>
                          <Text className="mt-1" size="1" color="gray">
                            realm_id:{" "}
                            <Text
                              as="span"
                              style={{ fontFamily: "var(--default-font-family-mono)" }}
                            >
                              {provider.oauth_data.realm_id || "Not linked"}
                            </Text>
                            {" | "}Connected at:{" "}
                            {provider.oauth_connected_at
                              ? new Date(provider.oauth_connected_at).toLocaleString()
                              : "N/A"}
                          </Text>
                          <Text size="1" color="gray">
                            Realm ID is populated automatically by the QuickBooks OAuth callback.
                          </Text>
                          <Text size="1" color="gray">
                            Last refresh:{" "}
                            {provider.oauth_last_refreshed_at
                              ? new Date(provider.oauth_last_refreshed_at).toLocaleString()
                              : "N/A"}
                          </Text>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => connectProvider(provider)}
                              disabled={!isEnabled}
                            >
                              {provider.oauth_connected ? "Reconnect" : "Connect"}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => refreshProvider(provider)}
                              disabled={!provider.oauth_connected}
                            >
                              Refresh Token
                            </Button>
                            <Button
                              type="button"
                              color="red"
                              onClick={() => disconnectProvider(provider)}
                              disabled={!provider.oauth_connected}
                            >
                              Disconnect
                            </Button>
                          </div>
                        </div>
                      )}

                      {errorByProvider[provider.provider] && (
                        <Callout.Root color="red" variant="soft" size="1">
                          <Callout.Text>{errorByProvider[provider.provider]}</Callout.Text>
                        </Callout.Root>
                      )}
                      {testMessageByProvider[provider.provider] && (
                        <Text size="2" color="gray">
                          {testMessageByProvider[provider.provider]}
                        </Text>
                      )}

                      <div className="flex items-center justify-between gap-3">
                        <Text size="1" color="gray">
                          {provider.configured_at
                            ? `Last updated: ${new Date(provider.configured_at).toLocaleString()}`
                            : "Not configured yet"}
                        </Text>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => testProvider(provider)}
                            disabled={isTesting || !isEnabled}
                          >
                            {isTesting ? "Testing..." : "Test"}
                          </Button>
                          <Button
                            type="button"
                            onClick={() => saveProvider(provider)}
                            disabled={isSaving}
                          >
                            {isSaving ? "Saving..." : "Save"}
                          </Button>
                        </div>
                      </div>
                    </Box>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </AppLayout>
    </>
  );
}
